export type MathSegment =
  | { type: 'text'; content: string }
  | { type: 'math'; content: string; display: boolean };

type MathContentRange = {
  start: number;
  end: number;
  content: string;
  display: boolean;
};

const ZWSP = '\u200B';

const isWhitespace = (value: string | undefined): boolean =>
  value !== undefined && /\s/.test(value);

const isEscapedDollar = (content: string, index: number): boolean => {
  let slashCount = 0;

  for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
    if (content[cursor] !== '\\') break;
    slashCount += 1;
  }

  return slashCount % 2 === 1;
};

const findClosingDollar = (content: string, start: number): number => {
  for (let index = start; index < content.length; index += 1) {
    if (content[index] === '$' && !isEscapedDollar(content, index)) {
      return index;
    }
  }

  return -1;
};

const isMathLikeNumericExpression = (content: string): boolean =>
  /[A-Za-z\\^_=+\-*/<>()[\]{}|]/.test(content);

const shouldParseInlineMath = (content: string): boolean => {
  if (!content || isWhitespace(content[0]) || isWhitespace(content.at(-1))) {
    return false;
  }

  if (content.includes('\n')) {
    return false;
  }

  if (/^\d/.test(content)) {
    return isMathLikeNumericExpression(content);
  }

  return true;
};

const normalizeZwspMathContent = (
  content: string,
): Pick<MathContentRange, 'content' | 'display'> => {
  if (content.startsWith('$$') && content.endsWith('$$')) {
    return {
      content: content.slice(2, -2),
      display: true,
    };
  }

  if (content.startsWith('$') && content.endsWith('$')) {
    return {
      content: content.slice(1, -1),
      display: false,
    };
  }

  return {
    content,
    display: false,
  };
};

const findMathContentRanges = (content: string): MathContentRange[] => {
  const ranges: MathContentRange[] = [];

  for (let index = 0; index < content.length; index += 1) {
    if (content[index] === ZWSP) {
      const end = content.indexOf(ZWSP, index + 1);

      if (end > index + 1) {
        ranges.push({
          start: index,
          end: end + 1,
          ...normalizeZwspMathContent(content.slice(index + 1, end)),
        });
        index = end;
      }

      continue;
    }

    if (content.startsWith('$$', index) && !isEscapedDollar(content, index)) {
      const end = content.indexOf('$$', index + 2);

      if (end >= index + 2) {
        ranges.push({
          start: index,
          end: end + 2,
          content: content.slice(index + 2, end),
          display: true,
        });
        index = end + 1;
      }

      continue;
    }

    if (content[index] !== '$' || isEscapedDollar(content, index)) {
      continue;
    }

    if (isWhitespace(content[index + 1])) {
      continue;
    }

    const end = findClosingDollar(content, index + 1);
    if (end === -1) continue;

    const inlineContent = content.slice(index + 1, end);
    if (!shouldParseInlineMath(inlineContent)) continue;

    ranges.push({
      start: index,
      end: end + 1,
      content: inlineContent,
      display: false,
    });
    index = end;
  }

  return ranges;
};

/**
 * Parse text content to extract math expressions and plain text segments.
 * Handles ZWSP (Zero-Width Space, \u200B) as delimiter around math expressions.
 *
 * Supported patterns:
 * - ZWSP-delimited: \u200B...\u200B (anything wrapped in ZWSP is treated as math)
 * - Display Math: $$...$$
 * - Inline Math: $...$
 * - Escaped Dollar: \$ is preserved as plain text
 *
 * @param content - The text content to parse (may contain ZWSP and LaTeX)
 * @returns Array of segments with type 'text' or 'math'
 */
export const parseMathContent = (content: string): MathSegment[] => {
  const ranges = findMathContentRanges(content);
  const segments: MathSegment[] = [];
  let cursor = 0;

  for (const range of ranges) {
    if (range.start > cursor) {
      segments.push({
        type: 'text',
        content: content.slice(cursor, range.start),
      });
    }

    segments.push({
      type: 'math',
      content: range.content,
      display: range.display,
    });
    cursor = range.end;
  }

  if (cursor < content.length) {
    segments.push({ type: 'text', content: content.slice(cursor) });
  }

  return segments;
};
